import { AxiosInstance, AxiosPromise } from 'axios';

import { AccessKeysApiFactory } from './clients/access-keys-api-factory';
import { ApaApiFactory } from './clients/apa-api-factory';
import { AuditLogApiFactory } from './clients/audit-log-api-factory';
import { AuthApiFactory } from './clients/auth-api-factory';
import { AutoscalerApiAdapter } from './clients/autoscaler-api-adapter';
import { BillingApiFactory } from './clients/billing-api-factory';
import { CloudInstancesApiFactory } from './clients/cloud-instances-api-factory';
import { ClusterApiAdapter } from './clients/cluster-api-adapter';
import { ClusterAutoscalerApiAdapter } from './clients/cluster-autoscaler-api-adapter';
import { ClusterManagementApiAdapter } from './clients/cluster-management-api-adapter';
import { CommitmentsApiAdapter } from './clients/commitments-api-adapter';
import { CostReportApiAdapter } from './clients/cost-report-api-adapter';
import { DboApiAdapter } from './clients/dbo-api-adapter';
import { DiscountsApiAdapter } from './clients/discounts-api-adapter';
import { EnterprisesApiAdapter } from './clients/enterprises-api-adapter';
import { FeatureFlagsApiFactory } from './clients/feature-flags-factory';
import { HubspotApiFactory } from './clients/hubspot-api-factory';
import { IdleResourcesApiAdapter } from './clients/idle-resources-api-adapter';
import { InventoryApiAdapter } from './clients/inventory-api-adapter';
import { KentApiAdapter } from './clients/kent-api-adapter';
import { LLMOptimizationApiFactory } from './clients/llm-optimization-api-factory';
import { NodeConfigTemplatesApiAdapter } from './clients/node-config-templates-api-adapter';
import { NotificationsApiAdapter } from './clients/notifications-api-adapter';
import { OmniApiAdapter } from './clients/omni-api-adapter';
import { OptimizationApiAdapter } from './clients/optimization-api-adapter';
import { OrganizationSettingsApiAdapter } from './clients/organization-settings-api-adapter';
import { PermissionsCenterApiFactory } from './clients/permissions-center-api-factory';
import { PlgTrialApiAdapter } from './clients/plg-trial-api-adapter';
import { PricingApiAdapter } from './clients/pricing-api-adapter';
import { RbacApiAdapter } from './clients/rbac-api-adapter';
import { ReportsApiAdapter } from './clients/reports-api-adapter';
import { RuntimeSecurityApiAdapter } from './clients/runtime-security-api-factory';
import { ScheduledRebalancingApiAdapter } from './clients/scheduled-rebalancing-api-adapter';
import { SecurityInsightsApiAdapter } from './clients/security-insights-api-adapter';
import { ServiceAccountsApiFactory } from './clients/service-accounts-api-factory';
import { ServicesApiAdapter } from './clients/services-api-adapter';
import { SSOApiAdapter } from './clients/sso-api-factory';
import { StopApiAdapter } from './clients/stop-api-adapter';
import { TrackingApiFactory } from './clients/tracking-api-factory';
import { UsersApiAdapter } from './clients/users-api-adapter';
import { WorkloadEvictionApiAdapter } from './clients/workload-eviction-api-adapter';
import { WorkloadOptimizationApiAdapter } from './clients/workload-optimization-api-adapter';
import { YamlPreviewApiAdapter } from './clients/yaml-preview-api-adapter';
import { setUpMiddleware } from './middleware/middleware';
import {
  ApiClient,
  type EndpointMiddleware,
  MiddlewareController,
} from './middleware/types';

const _makeConsoleApiClient = (client: AxiosInstance) => {
  return {
    auth: AuthApiFactory(client),
    accessKeys: AccessKeysApiFactory(client),
    apa: ApaApiFactory(client),
    hubspot: HubspotApiFactory(client),
    featureFlags: FeatureFlagsApiFactory(client),
    auditLog: AuditLogApiFactory(client),
    billing: BillingApiFactory(client),
    costReport: CostReportApiAdapter(client),
    cloudInstances: CloudInstancesApiFactory(client),
    clusters: ClusterApiAdapter(client),
    clusterManagement: ClusterManagementApiAdapter(client),
    autoscaler: AutoscalerApiAdapter(client),
    scheduledRebalancing: ScheduledRebalancingApiAdapter(client),
    securityInsights: SecurityInsightsApiAdapter(client),
    runtimeSecurity: RuntimeSecurityApiAdapter(client),
    user: UsersApiAdapter(client),
    reports: ReportsApiAdapter(client),
    nodeConfigTemplates: NodeConfigTemplatesApiAdapter(client),
    notifications: NotificationsApiAdapter(client),
    commitments: CommitmentsApiAdapter(client),
    workloadOptimization: WorkloadOptimizationApiAdapter(client),
    sso: SSOApiAdapter(client),
    yamlPreview: YamlPreviewApiAdapter(client),
    llmOptimization: LLMOptimizationApiFactory(client),
    dbo: DboApiAdapter(client),
    discounts: DiscountsApiAdapter(client),
    pricing: PricingApiAdapter(client),
    rbac: RbacApiAdapter(client),
    inventory: InventoryApiAdapter(client),
    serviceAccounts: ServiceAccountsApiFactory(client),
    services: ServicesApiAdapter(client),
    omni: OmniApiAdapter(client),
    enterprises: EnterprisesApiAdapter(client),
    clusterAutoscaler: ClusterAutoscalerApiAdapter(client),
    organizationSettings: OrganizationSettingsApiAdapter(client),
    plgTrial: PlgTrialApiAdapter(client),
    servicesTracking: TrackingApiFactory(client),
    idleResources: IdleResourcesApiAdapter(client),
    kent: KentApiAdapter(client),
    optimization: OptimizationApiAdapter(client),
    permissionsCenter: PermissionsCenterApiFactory(client),
    stop: StopApiAdapter(client),
    workloadEviction: WorkloadEvictionApiAdapter(client),
  };
};

export type ConsoleApiClient = ApiClient<
  ReturnType<typeof _makeConsoleApiClient>
>;

export type FeatureMiddleware<FEATURE extends keyof ConsoleApiClient> =
  EndpointMiddleware<ConsoleApiClient>[FEATURE];

export type EndpointReturnType<
  FEATURE extends keyof ConsoleApiClient,
  ENDPOINT extends keyof ConsoleApiClient[FEATURE]
> = ConsoleApiClient[FEATURE][ENDPOINT] extends (
  ...args: any[]
) => AxiosPromise<infer RETURN_TYPE>
  ? RETURN_TYPE
  : unknown;

export type EndpointParameters<
  FEATURE extends keyof ConsoleApiClient,
  ENDPOINT extends keyof ConsoleApiClient[FEATURE]
> = ConsoleApiClient[FEATURE][ENDPOINT] extends (...args: infer ARGS) => any
  ? ARGS
  : unknown;

export const makeConsoleApiClient = (
  client: AxiosInstance
): {
  apiClient: ConsoleApiClient;
  middlewareController: MiddlewareController<ConsoleApiClient>;
} => {
  const { apiClient, middlewareController } = setUpMiddleware(
    _makeConsoleApiClient(client)
  );

  return { apiClient, middlewareController };
};
